import type React from "react";
import { cx } from "@emotion/css";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Avatar } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { createRef } from "react";
import { use100vh } from "react-div-100vh";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { FixedSizeList } from "react-window";
import Progress from "src/components/ExamComponents/Progress";
import { isMultiAnswersExam } from "src/helpers/examHelpers";
import isMobile, { isTablet } from "src/helpers/isMobile";
import { getExam } from "src/hooks/useExam";
import { useFontSize } from "src/hooks/useFontSize";
import { AnswerType, ExamStatus } from "src/types/enums";

import { IconButton } from "../Default/LekUI/Button";
import { SwipeableContent } from "../Default/LekUI/SwipableDrawer";

interface DrawerQuestion {
	id: string | number;
	pytanie: string;
	answer: string | null;
	correctAnswer: string;
	isCorrectAnswer: boolean;
	isCanceled: boolean;
	answers: Record<string, string>;
	images?: Array<{ url: string; [key: string]: unknown }>;
	excludedAnswers: Array<{ answer: string }>;
}

interface QuestionDrawerProps {
	selected: DrawerQuestion;
	onSelect: (item: DrawerQuestion) => void;
	items: DrawerQuestion[];
	isOpen: boolean;
	toggleIsMenuOpen: () => void;
	lastAnsweredQuestionIndex: number;
	examId: string;
	viewOnly?: boolean;
	examName?: string;
	className?: string;
}

function QuestionDrawer(props: QuestionDrawerProps) {
	const { fontSizeClass } = useFontSize();
	const { t } = useTranslation();
	const height = use100vh();
	const listRef = createRef<FixedSizeList>();
	const { items } = props;
	const { id } = useParams<{ id: string }>();
	const exam = getExam(id);
	const { stats } = exam;

	const onSelect = (item: DrawerQuestion) => {
		props.onSelect(item);
		if (isTablet()) {
			props.toggleIsMenuOpen();
		}
	};

	const getAnswerType = (item: DrawerQuestion): string => {
		if (!item.answer) {
			return "default";
		}

		if (isMultiAnswersExam(exam.type) && exam.status === ExamStatus.ACTIVE) {
			return AnswerType.HIDDEN;
		}

		if (item.isCanceled) {
			return AnswerType.WARNING;
		}

		return item.isCorrectAnswer ? AnswerType.CORRECT : AnswerType.WRONG;
	};

	const renderItem = ({ index, style }: { index: number; style: React.CSSProperties }) => {
		const item = items[index];

		return (
			<ListItemButton
				id={`question-${item.id}`}
				className="question--drawer--item"
				key={item.id}
				selected={props.selected.id === item.id}
				onClick={() => onSelect(item)}
				style={style}
			>
				<ListItemAvatar className="w-min-unset mr-8">
					<Avatar
						className={`square-24 size-12-light text-black answer--${getAnswerType(
							item,
						)}`}
					>
						<span>{index + 1}</span>
					</Avatar>
				</ListItemAvatar>
				<ListItemText
					primary={
						<span
							className={cx("text-ellipsis-1 h-100", fontSizeClass)}
							dangerouslySetInnerHTML={{
								__html: item.pytanie.replace(/<[^>]*>/gi, ""),
							}}
						/>
					}
				/>
			</ListItemButton>
		);
	};

	const renderProgress = () => {
		if (!isMobile()) {
			return null;
		}

		return (
			<div className="px-12 py-8">
				<Progress
					disablePercent
					allItems={stats.totalQuestions}
					resolvedItems={stats.resolvedQuestions}
					unit={t("units.questions")}
				/>
			</div>
		);
	};

	const renderItems = () => {
		return (
			<FixedSizeList
				height={height - (isMobile() ? 140 : isTablet() ? 0 : 40)}
				itemCount={items.length}
				itemSize={35}
				ref={listRef}
			>
				{renderItem}
			</FixedSizeList>
		);
	};

	const renderContent = (children: React.ReactNode) => {
		if (isMobile()) {
			return (
				<SwipeableContent
					isOpen={props.isOpen}
					onClose={props.toggleIsMenuOpen}
				>
					{children}
				</SwipeableContent>
			);
		}

		if (isTablet()) {
			return (
				<Drawer
					variant="temporary"
					open={props.isOpen}
					onClose={props.toggleIsMenuOpen}
					sx={{ "& .MuiDrawer-paper": { width: "380px" } }}
				>
					{children}
				</Drawer>
			);
		}

		return (
			<Drawer
				variant="permanent"
				open={props.isOpen}
				className={cx(props.className, "question--drawer", {
					"question--drawer--closed": !props.isOpen,
				})}
			>
				{children}
			</Drawer>
		);
	};

	const renderToggle = () => {
		return (
			<div className="question--drawer--toggle">
				<IconButton onClick={props.toggleIsMenuOpen} className="p-0">
					{props.isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
				</IconButton>
			</div>
		);
	};

	return renderContent(
		<>
			{renderToggle()}
			{renderProgress()}
			{renderItems()}
		</>,
	);
}

export default QuestionDrawer;
